import type { CandidateFormState } from "./CandidateFormFields";

export type AddTarget =
  | { kind: "org" }
  | { kind: "talent_pool"; talentPoolId: string }
  | { kind: "campaign"; campaignId: string; stage: string };

export interface CampaignMembership {
  id: string;
  stage: string;
  campaign: { id: string; title: string };
}

export interface PoolMembership {
  id: string;
  talentPool: { id: string; name: string };
}

export interface SearchCandidate {
  id: string;
  referenceNumber: number | null;
  fullName: string;
  email: string | null;
  currentRoleTitle: string | null;
  currentCompany: string | null;
  location: string | null;
  campaignCandidates: CampaignMembership[];
  talentPoolMemberships: PoolMembership[];
}

export type TargetMembership = { kind: "campaign"; stage: string } | { kind: "talent_pool" };

const LINKEDIN_PROFILE_RE = /^https?:\/\/(www\.)?linkedin\.com\/in\/([^\s/?#]+)\/?(?:[?#].*)?$/i;

/**
 * Recognise a LinkedIn profile URL and return its canonical form
 * (`https://www.linkedin.com/in/<slug>`), matching what the server stores.
 * Company pages, other LinkedIn paths and non-LinkedIn text return null.
 */
export function parseLinkedinUrl(input: string): string | null {
  const match = LINKEDIN_PROFILE_RE.exec(input.trim());
  if (!match) return null;
  return `https://www.linkedin.com/in/${match[2].toLowerCase()}`;
}

export function parseNameFromSearch(search: string): { firstName: string; lastName: string } {
  const parts = search.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { firstName: "", lastName: "" };
  if (parts.length === 1) return { firstName: parts[0], lastName: "" };
  return { firstName: parts[0], lastName: parts.slice(1).join(" ") };
}

export function buildAddTo(target: AddTarget) {
  switch (target.kind) {
    case "campaign":
      return { campaignId: target.campaignId, stage: target.stage };
    case "talent_pool":
      return { talentPoolId: target.talentPoolId };
    case "org":
      return undefined;
  }
}

/** The createCandidate input for a manually entered form. Never carries a LinkedIn URL. */
export function buildCandidateInput(form: CandidateFormState) {
  const input: Record<string, unknown> = {
    firstName: form.firstName,
    lastName: form.lastName,
  };
  if (form.email) input.email = form.email;
  if (form.currentRoleTitle) input.currentRoleTitle = form.currentRoleTitle;
  if (form.currentCompany) input.currentCompany = form.currentCompany;
  if (form.location) input.location = form.location;
  return input;
}

/**
 * Whether the candidate is already in the target campaign or pool. A campaign
 * target only matches a CampaignCandidate row — someone sitting in the
 * campaign's pool is not on the pipeline and can still be added.
 */
export function membershipOf(
  candidate: Pick<SearchCandidate, "campaignCandidates" | "talentPoolMemberships">,
  target: AddTarget,
): TargetMembership | undefined {
  switch (target.kind) {
    case "campaign": {
      const cc = candidate.campaignCandidates.find((m) => m.campaign.id === target.campaignId);
      return cc ? { kind: "campaign", stage: cc.stage } : undefined;
    }
    case "talent_pool": {
      const found = candidate.talentPoolMemberships.some(
        (m) => m.talentPool.id === target.talentPoolId,
      );
      return found ? { kind: "talent_pool" } : undefined;
    }
    case "org":
      return undefined;
  }
}

export function describeMembership(membership: TargetMembership): string {
  return membership.kind === "campaign"
    ? `Already in this campaign · ${membership.stage}`
    : "Already in this pool";
}
